/**
 * src/lib/offline/photoApplier.js — the offline 'photos.create' drain step.
 *
 * Spec: docs/plans/2026-06-05-photos-subsystem-design.md (v2.7 Task 1, flow [B]).
 *
 * A queued capture op carries the image Blob + scope + product_types +
 * content_sha256 in its payload. On reconnect the sync engine calls this to:
 *   1. upload the Blob to the private `photo-buffer` Supabase bucket
 *      (path = <scope>/<sha>.<ext>), upsert so a replay overwrites identically;
 *   2. POST photos.register with the buffer_path + scope + product_types + sha.
 *
 * Idempotent: the server dedups on content_sha256 (returns deduped:true), and the
 * bucket upload is upsert. A double-drain therefore lands ONE row + one object.
 * Returns a { success, error } shape so the sync engine's existing dup/network
 * handling (DUP_RE / isNetworkErr) applies uniformly with the other appliers.
 *
 * The Supabase client + register call are injectable for tests.
 */

import supabaseClient from '../supabase';
import { registerPhoto as registerPhotoApi } from '../api/photos';

const BUCKET = 'photo-buffer';
const THUMB_BUCKET = 'photo-thumbs';
const DUP_MSG = 'duplicate key value violates unique constraint (content_sha256)';

function extFor(mime, fileName) {
  if (fileName && fileName.includes('.')) return fileName.split('.').pop().toLowerCase();
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/heic' || mime === 'image/heif') return 'heic';
  return 'jpg';
}

/**
 * TICKET-578: outbox.enqueue persists binary fields as { __bytes, type, name }
 * (see the note there — a stored File is a dead pointer by drain time). Rebuild a
 * real Blob here. A legacy op queued before this shipped still carries a raw Blob,
 * so both shapes are accepted.
 */
function asBlob(v) {
  if (!v) return null;
  if (v.__bytes) return new Blob([v.__bytes], { type: v.type || 'application/octet-stream' });
  return v;
}

// TICKET-1404 (#2260): the AI description is written in the app language. The
// applier runs in the sync engine (no React context), so it reads the same persisted
// value useT() serves (I18nContext STORAGE_KEY 'acn_locale'). An op that carries its own
// `language` wins. Unknown/unreadable -> undefined, and the server keeps its default.
const APP_LOCALE_KEY = 'acn_locale';
const DESCRIBE_LANGUAGES = ['en', 'es'];
export function photoLanguage(payload = {}) {
  if (DESCRIBE_LANGUAGES.includes(payload.language)) return payload.language;
  try {
    const stored = globalThis.localStorage?.getItem(APP_LOCALE_KEY);
    return DESCRIBE_LANGUAGES.includes(stored) ? stored : undefined;
  } catch {
    return undefined; // spine-emit-ok: storage blocked (private mode) -> server default language
  }
}

function scopePrefix(payload) {
  return payload.project_id
    ? `project/${payload.project_id}`
    : payload.deal_id
      ? `deal/${payload.deal_id}`
      : payload.vessel_id
        ? `vessel/${payload.vessel_id}`
        : 'unscoped';
}

/**
 * Upload the queued Blob to photo-buffer, then register the photo row.
 *
 * @param {object} payload
 * @param {Blob} payload.blob — REQUIRED captured image bytes
 * @param {string} payload.content_sha256 — REQUIRED dedup id
 * @param {string} [payload.project_id|deal_id|vessel_id]
 * @param {string[]} [payload.product_types]
 * @param {string} [payload.mime_type]
 * @param {string} [payload.file_name]
 * @param {number} [payload.file_size]
 * @param {number} [payload.width_px]
 * @param {number} [payload.height_px]
 * @param {object} [payload.exif]
 * @param {string} [payload.caption]
 * @param {'en'|'es'} [payload.language] — app language for the AI description (TICKET-1404)
 * @param {object} [deps] — { supabaseImpl, registerImpl } (tests)
 * @returns {Promise<{success:boolean, document_id?:string, deduped?:boolean, error?:string}>}
 */
export async function uploadPhotoBufferAndRegister(payload = {}, deps = {}) {
  const supabase = deps.supabaseImpl || supabaseClient;
  const registerPhoto = deps.registerImpl || registerPhotoApi;

  const sha = payload.content_sha256;
  const blob = asBlob(payload.blob);
  if (!sha) return { success: false, error: 'content_sha256 required' };
  if (!blob) return { success: false, error: 'blob required' };
  // A zero-byte body is the dead-File-pointer failure mode (TICKET-578). Say so
  // instead of letting Storage answer the opaque `No content provided` 8 times.
  if (!blob.size) return { success: false, error: 'blob empty (0 bytes) — capture lost before sync' };

  const mime = payload.mime_type || blob.type || 'image/jpeg';
  const ext = extFor(mime, payload.file_name);
  const objectPath = `${scopePrefix(payload)}/${sha}.${ext}`;
  const bufferPath = `${BUCKET}/${objectPath}`;

  // 1) Buffer upload (upsert — a replay overwrites the identical object).
  // TICKET-658: pass raw bytes, NOT the Blob — storage-js wraps a Blob body in
  // FormData with an EMPTY field name (`body.append('', fileBody)`), which
  // WebKit/Safari intermittently mis-serializes on multi-MB payloads, landing
  // as "No content provided" server-side despite a correct client-side size.
  // An ArrayBuffer body skips FormData entirely (raw body + explicit
  // Content-Type header) — a different, unaffected code path in the same lib.
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(objectPath, await blob.arrayBuffer(), { contentType: mime, upsert: true, cacheControl: '3600' });
  // A unique/dup error here means the same physical photo already buffered →
  // surface it as a create-dup so the sync engine drops the op (already synced).
  if (upErr && !/duplicate|already exists|conflict/i.test(upErr.message || '')) {
    // TICKET-654: the local blob.size guard above already ruled out an empty
    // capture, so a server-side "No content provided" here means the bytes were
    // lost IN TRANSIT (IndexedDB round-trip / Blob re-wrap), not at capture. Log
    // the rebuilt blob's size + whether it came from the durable __bytes path so
    // the NEXT occurrence pinpoints where the body went empty instead of
    // repeating this same unexplained drop for a 6th time.
    return {
      success: false,
      error: `buffer upload failed: ${upErr.message} (rebuilt_blob_size=${blob.size}, from_durable_bytes=${!!payload.blob?.__bytes})`,
    };
  }

  // 1b) Thumbnail upload to the PUBLIC photo-thumbs tier so the synced gallery
  // tile renders directly (the original stays private in photo-buffer, served
  // on-demand). Best-effort: a thumb failure must NEVER fail the capture — the
  // tile just falls back to a placeholder. upsert = replay-safe (same sha).
  let thumbPath = payload.thumb_path || undefined;
  const thumbBlob = asBlob(payload.thumb_blob);
  if (thumbBlob) {
    const thumbObjectPath = `${scopePrefix(payload)}/${sha}.jpg`;
    // TICKET-658: same ArrayBuffer-not-Blob fix as the buffer upload above.
    const { error: thErr } = await supabase.storage
      .from(THUMB_BUCKET)
      .upload(thumbObjectPath, await thumbBlob.arrayBuffer(), { contentType: 'image/jpeg', upsert: true, cacheControl: '3600' });
    if (!thErr || /duplicate|already exists|conflict/i.test(thErr.message || '')) {
      // photo-thumbs is public — persist the full public URL so thumbFromMetadata
      // (which requires an http(s) path) renders it without a signed request.
      const { data: pub } = supabase.storage.from(THUMB_BUCKET).getPublicUrl(thumbObjectPath);
      thumbPath = pub?.publicUrl || thumbPath;
    }
  }

  // 2) Register the row (server idempotent on content_sha256).
  const res = await registerPhoto({
    project_id: payload.project_id || undefined,
    deal_id: payload.deal_id || undefined,
    vessel_id: payload.vessel_id || undefined,
    bundle_id: payload.bundle_id || undefined, // TICKET-734 capture-in-bundle
    product_types: Array.isArray(payload.product_types) ? payload.product_types : [],
    content_sha256: sha,
    buffer_path: bufferPath,
    thumb_path: thumbPath,
    file_name: payload.file_name || `${sha}.${ext}`,
    file_size: payload.file_size || blob.size || 0,
    mime_type: mime,
    width_px: payload.width_px || undefined,
    height_px: payload.height_px || undefined,
    exif: payload.exif || undefined,
    // Passthrough GPS URL extracted from EXIF before canvas strip (TICKET-PHOTO-GEO S2).
    exif_gps_map_url: payload.exif_gps_map_url || undefined,
    caption: payload.caption || undefined,
    language: photoLanguage(payload),
  });

  if (res && res.success === false) {
    return { success: false, error: res.error || DUP_MSG };
  }
  return { success: true, document_id: res?.document_id, deduped: !!res?.deduped };
}
